// Email Connection Card Component
// Displays OAuth connection status and provides connect/disconnect buttons
//
// ⚠️ IMPORTANT: This uses CUSTOM OAuth flows (NOT Blink SDK)
// - Gmail: Uses Google OAuth 2.0 API directly (https://accounts.google.com/o/oauth2/v2/auth)
// - Outlook: Uses Microsoft Azure AD OAuth 2.0 (https://login.microsoftonline.com/common/oauth2/v2.0/authorize)
// - State is saved in localStorage and verified in OAuthCallback
// - Tokens are securely stored in database via backend functions
//
// This is separate from app authentication (Login page uses Blink SDK)

import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, AlertCircle, Link as LinkIcon, Unlink } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  initiateGmailOAuth, 
  initiateOutlookOAuth,
  getConnectionStatus,
  isOAuthConfigured,
  handleOAuthCallback
} from '../services/oauthService';
import { saveOAuthConnection, removeOAuthConnection, getUserOAuthConnections } from '../lib/oauthConnections';
import { blink } from '../lib/blink';
import { toast } from 'sonner';

// 🔧 Using centralized Blink client from /lib/blink.ts
// This ensures mailbox OAuth doesn't interfere with the main app's auth state

interface EmailConnectionCardProps {
  onConnectionChange?: () => void;
}

const EmailConnectionCard: React.FC<EmailConnectionCardProps> = ({ onConnectionChange }) => {
  const [user, setUser] = useState<any>(null);
  const [connecting, setConnecting] = useState<'gmail' | 'outlook' | null>(null);
  const [status, setStatus] = useState<{ gmail: boolean; outlook: boolean }>({ gmail: false, outlook: false });
  const [oauthConfig, setOauthConfig] = useState({
    gmail: isOAuthConfigured('gmail'),
    outlook: isOAuthConfigured('outlook')
  });
  const [dbConnections, setDbConnections] = useState<any[]>([]);
  // 🔧 FIX: Track multiple accounts per provider
  const [gmailAccounts, setGmailAccounts] = useState<any[]>([]);
  const [outlookAccounts, setOutlookAccounts] = useState<any[]>([]);
  const [lastSyncTimes, setLastSyncTimes] = useState<{ gmail?: string; outlook?: string }>({});

  useEffect(() => {
    // Listen for auth state
    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user && !state.isLoading) {
        setUser(state.user);
        await loadConnections(state.user.id);
        await loadLastSyncTimes(state.user.id);
        
        // Load connection status from database
        const status = await getConnectionStatus(state.user.id);
        setStatus(status);
      }
    });

    // Set OAuth config
    setOauthConfig({
      gmail: isOAuthConfigured('gmail'),
      outlook: isOAuthConfigured('outlook')
    });

    return unsubscribe;
  }, []);

  const loadConnections = async (userId: string) => {
    try {
      const connections = await getUserOAuthConnections(userId);
      setDbConnections(connections);
      
      // 🔧 FIX: Load all email accounts (not just one per provider)
      const accounts = await blink.db.emailAccounts.list({
        where: { userId },
        limit: 50 // Allow up to 50 accounts
      });
      
      // Separate by provider
      const gmail = accounts.filter((acc: any) => acc.provider === 'gmail');
      const outlook = accounts.filter((acc: any) => acc.provider === 'outlook');
      
      setGmailAccounts(gmail);
      setOutlookAccounts(outlook);
      
      console.log(`✅ Loaded ${gmail.length} Gmail accounts and ${outlook.length} Outlook accounts`);
    } catch (error) {
      console.error('Failed to load connections:', error);
    }
  };

  const loadLastSyncTimes = async (userId: string) => {
    try {
      const accounts = await blink.db.emailAccounts.list({
        where: { userId },
        limit: 10
      });
      
      const syncTimes: { gmail?: string; outlook?: string } = {};
      
      for (const account of accounts) {
        if (account.lastSyncAt) {
          if (account.provider === 'gmail') {
            syncTimes.gmail = account.lastSyncAt;
          } else if (account.provider === 'outlook') {
            syncTimes.outlook = account.lastSyncAt;
          }
        }
      }
      
      setLastSyncTimes(syncTimes);
    } catch (error) {
      console.error('Failed to load sync times:', error);
    }
  };

  // Sync Gmail emails and calendar
  const syncGmailData = async (accessToken: string, userId: string) => {
    const errors: string[] = [];
    const successes: string[] = [];

    try {
      // Sync emails
      try {
        // Get accountId for updating lastSyncAt
        const accounts = await blink.db.emailAccounts.list({
          where: { userId, provider: 'gmail' },
          limit: 1
        });
        const accountId = accounts.length > 0 ? accounts[0].id : undefined;
        
        const emailResponse = await fetch('https://ben3ou87--gmail-sync.functions.blink.new', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken, userId, maxResults: 100, accountId })
        });

        if (emailResponse.ok) {
          const emailData = await emailResponse.json();
          successes.push(`${emailData.synced} emails`);
          
          // Update last sync time in UI
          if (emailData.lastSyncAt) {
            setLastSyncTimes(prev => ({ ...prev, gmail: emailData.lastSyncAt }));
          }
        } else {
          const error = await emailResponse.json();
          errors.push(`Email sync: ${error.error || 'Unknown error'}`);
        }
      } catch (err) {
        errors.push(`Email sync: ${err instanceof Error ? err.message : 'Network error'}`);
      }

      // Sync calendar
      try {
        const calendarResponse = await fetch('https://ben3ou87--calendar-sync.functions.blink.new', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: 'gmail', accessToken, userId, maxResults: 50 })
        });

        if (calendarResponse.ok) {
          const calendarData = await calendarResponse.json();
          successes.push(`${calendarData.synced} events`);
        } else {
          const error = await calendarResponse.json();
          errors.push(`Calendar sync: ${error.error || 'Unknown error'}`);
        }
      } catch (err) {
        errors.push(`Calendar sync: ${err instanceof Error ? err.message : 'Network error'}`);
      }

      // Show consolidated status
      if (successes.length > 0) {
        toast.success(`✅ Gmail synced successfully: ${successes.join(', ')}`);
      }
      if (errors.length > 0) {
        toast.error(`⚠️ Gmail sync partial: ${errors.join('; ')}`);
      }
    } catch (error) {
      // Should not reach here, but handle unexpected errors
      console.error('Unexpected Gmail sync error:', error);
      toast.error('Failed to complete Gmail sync');
    }
  };

  // Sync Outlook emails and calendar
  const syncOutlookData = async (accessToken: string, userId: string) => {
    const errors: string[] = [];
    const successes: string[] = [];

    try {
      // Sync emails
      try {
        // Get accountId for updating lastSyncAt
        const accounts = await blink.db.emailAccounts.list({
          where: { userId, provider: 'outlook' },
          limit: 1
        });
        const accountId = accounts.length > 0 ? accounts[0].id : undefined;
        
        const emailResponse = await fetch('https://ben3ou87--outlook-sync.functions.blink.new', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken, userId, maxResults: 100, accountId })
        });

        if (emailResponse.ok) {
          const emailData = await emailResponse.json();
          successes.push(`${emailData.synced} emails`);
          
          // Update last sync time in UI
          if (emailData.lastSyncAt) {
            setLastSyncTimes(prev => ({ ...prev, outlook: emailData.lastSyncAt }));
          }
        } else {
          const error = await emailResponse.json();
          errors.push(`Email sync: ${error.error || 'Unknown error'}`);
        }
      } catch (err) {
        errors.push(`Email sync: ${err instanceof Error ? err.message : 'Network error'}`);
      }

      // Sync calendar
      try {
        const calendarResponse = await fetch('https://ben3ou87--calendar-sync.functions.blink.new', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: 'outlook', accessToken, userId, maxResults: 50 })
        });

        if (calendarResponse.ok) {
          const calendarData = await calendarResponse.json();
          successes.push(`${calendarData.synced} events`);
        } else {
          const error = await calendarResponse.json();
          errors.push(`Calendar sync: ${error.error || 'Unknown error'}`);
        }
      } catch (err) {
        errors.push(`Calendar sync: ${err instanceof Error ? err.message : 'Network error'}`);
      }

      // Show consolidated status
      if (successes.length > 0) {
        toast.success(`✅ Outlook synced successfully: ${successes.join(', ')}`);
      }
      if (errors.length > 0) {
        toast.error(`⚠️ Outlook sync partial: ${errors.join('; ')}`);
      }
    } catch (error) {
      // Should not reach here, but handle unexpected errors
      console.error('Unexpected Outlook sync error:', error);
      toast.error('Failed to complete Outlook sync');
    }
  };

  const handleConnectGmail = async () => {
    if (!user) {
      toast.error('Please sign in to connect Gmail');
      return;
    }

    setConnecting('gmail');
    
    // Add timeout to prevent stuck state
    const timeoutId = setTimeout(() => {
      if (connecting === 'gmail') {
        setConnecting(null);
        toast.error('Connection timeout - please try again');
      }
    }, 120000); // 2 minute timeout

    try {
      const tokens = await initiateGmailOAuth();
      clearTimeout(timeoutId);
      
      // Save connection to database (may already be saved by OAuth callback)
      if (tokens.email) {
        try {
          await saveOAuthConnection(user.id, 'gmail', tokens.email);
        } catch (saveError) {
          console.error('Failed to save connection metadata:', saveError);
          // Continue anyway - tokens are already saved by callback
        }
      }
      
      // Refresh both localStorage status and database connections
      await loadConnections(user.id);
      const newStatus = await getConnectionStatus(user.id);
      setStatus(newStatus);
      
      // Automatically sync emails and calendar after successful connection
      toast.success('Gmail connected! Syncing emails and calendar...');
      
      // Run sync in background - don't block on it
      syncGmailData(tokens.accessToken, user.id)
        .then(() => loadLastSyncTimes(user.id))
        .catch(err => {
          console.error('Background sync failed:', err);
          toast.error('Connected, but sync failed. Try manual sync later.');
        });
      
      onConnectionChange?.();
    } catch (error) {
      clearTimeout(timeoutId);
      const errorMsg = error instanceof Error ? error.message : 'Failed to connect Gmail';
      
      // Check if it's a Google OAuth compliance error
      const isComplianceError = errorMsg.toLowerCase().includes('oauth 2.0 policy') || 
                                errorMsg.toLowerCase().includes('doesn\'t comply') ||
                                errorMsg.toLowerCase().includes('invalid_request');
      
      if (isComplianceError) {
        // Show user-friendly error message
        toast.error(
          'Gmail OAuth Verification Required',
          {
            description: 'Add your email as a test user in Google Cloud Console (OAuth Consent Screen → Test Users) to proceed with development.',
            duration: 8000,
          }
        );
        
        // Log concise instructions to console (single block)
        console.groupCollapsed('🔒 Google OAuth Setup Instructions');
        console.log('This app uses sensitive Gmail scopes that require verification.');
        console.log('');
        console.log('QUICK FIX (Development/Testing):');
        console.log('1. Visit: https://console.cloud.google.com/apis/credentials/consent');
        console.log('2. OAuth Consent Screen → Test Users → ADD USERS');
        console.log('3. Add: ' + (user.email || '[your-email]'));
        console.log('4. Save and retry connection');
        console.log('');
        console.log('For production: Submit app for Google verification (7-14 days)');
        console.log('Details: https://support.google.com/cloud/answer/9110914');
        console.groupEnd();
      } else {
        toast.error(errorMsg, { duration: 6000 });
        console.error('Gmail OAuth error:', error);
      }
    } finally {
      setConnecting(null);
    }
  };

  const handleConnectOutlook = async () => {
    if (!user) {
      toast.error('Please sign in to connect Outlook');
      return;
    }

    setConnecting('outlook');
    
    // Add timeout to prevent stuck state
    const timeoutId = setTimeout(() => {
      if (connecting === 'outlook') {
        setConnecting(null);
        toast.error('Connection timeout - please try again');
      }
    }, 120000); // 2 minute timeout

    try {
      const tokens = await initiateOutlookOAuth();
      clearTimeout(timeoutId);
      
      // Save connection to database (may already be saved by OAuth callback)
      if (tokens.email) {
        try {
          await saveOAuthConnection(user.id, 'outlook', tokens.email);
        } catch (saveError) {
          console.error('Failed to save connection metadata:', saveError);
          // Continue anyway - tokens are already saved by callback
        }
      }
      
      // Refresh both localStorage status and database connections
      await loadConnections(user.id);
      const newStatus = await getConnectionStatus(user.id);
      setStatus(newStatus);
      
      // Automatically sync emails and calendar after successful connection
      toast.success('Outlook connected! Syncing emails and calendar...');
      
      // Run sync in background - don't block on it
      syncOutlookData(tokens.accessToken, user.id)
        .then(() => loadLastSyncTimes(user.id))
        .catch(err => {
          console.error('Background sync failed:', err);
          toast.error('Connected, but sync failed. Try manual sync later.');
        });
      
      onConnectionChange?.();
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Outlook OAuth error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to connect Outlook');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider: 'gmail' | 'outlook') => {
    if (!user) return;

    try {
      await removeOAuthConnection(user.id, provider);
      await loadConnections(user.id);
      const status = await getConnectionStatus(user.id);
      setStatus(status);
      onConnectionChange?.();
      toast.success(`${provider === 'gmail' ? 'Gmail' : 'Outlook'} disconnected`);
    } catch (error) {
      console.error('Failed to disconnect:', error);
      toast.error('Failed to disconnect account');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Email & Calendar Accounts</h3>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Connect your accounts to enable real-time email rendering, calendar sync, and AI-powered features. 
        <span className="font-medium text-foreground"> Connecting your email automatically syncs both emails and calendar events.</span>
      </p>

      <div className="space-y-3">
        {/* Gmail Connection */}
        <div className="rounded-lg border border-border/50 bg-background/50">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h4 className="font-medium">Gmail</h4>
                <p className="text-xs text-muted-foreground">
                  {gmailAccounts.length > 0 ? (
                    <span className="text-green-600 font-medium">{gmailAccounts.length} account{gmailAccounts.length > 1 ? 's' : ''} connected</span>
                  ) : (
                    'Email & Calendar - Not connected'
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnectGmail}
                disabled={connecting === 'gmail' || !oauthConfig.gmail}
                title={!oauthConfig.gmail ? 'Gmail OAuth not configured' : 'Add another Gmail account'}
              >
                {connecting === 'gmail' ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    {gmailAccounts.length > 0 ? 'Add Account' : (oauthConfig.gmail ? 'Connect' : 'Coming Soon')}
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Show connected Gmail accounts */}
          {gmailAccounts.length > 0 && (
            <div className="border-t border-border/50 p-3 space-y-2">
              {gmailAccounts.map((account: any) => (
                <div key={account.id} className="flex items-center justify-between p-2 rounded bg-background/50">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                    <span className="text-sm">{account.emailAddress}</span>
                    {account.lastSyncAt && (
                      <span className="text-xs text-muted-foreground">
                        • Last synced: {new Date(account.lastSyncAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDisconnect('gmail')}
                    title="Disconnect this account"
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Outlook Connection */}
        <div className="rounded-lg border border-border/50 bg-background/50">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h4 className="font-medium">Outlook</h4>
                <p className="text-xs text-muted-foreground">
                  {outlookAccounts.length > 0 ? (
                    <span className="text-green-600 font-medium">{outlookAccounts.length} account{outlookAccounts.length > 1 ? 's' : ''} connected</span>
                  ) : (
                    'Email & Calendar - Not connected'
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnectOutlook}
                disabled={connecting === 'outlook' || !oauthConfig.outlook}
                title={!oauthConfig.outlook ? 'Outlook OAuth not configured' : 'Add another Outlook account'}
              >
                {connecting === 'outlook' ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    {outlookAccounts.length > 0 ? 'Add Account' : (oauthConfig.outlook ? 'Connect' : 'Coming Soon')}
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Show connected Outlook accounts */}
          {outlookAccounts.length > 0 && (
            <div className="border-t border-border/50 p-3 space-y-2">
              {outlookAccounts.map((account: any) => (
                <div key={account.id} className="flex items-center justify-between p-2 rounded bg-background/50">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                    <span className="text-sm">{account.emailAddress}</span>
                    {account.lastSyncAt && (
                      <span className="text-xs text-muted-foreground">
                        • Last synced: {new Date(account.lastSyncAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDisconnect('outlook')}
                    title="Disconnect this account"
                  >
                    <Unlink className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {gmailAccounts.length === 0 && outlookAccounts.length === 0 && (
        <div className="mt-4 space-y-3">
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
              <div className="text-xs text-amber-500">
                <p className="font-medium mb-1">
                  {!oauthConfig.gmail && !oauthConfig.outlook 
                    ? 'OAuth Configuration Required' 
                    : 'No accounts connected'}
                </p>
                <p className="opacity-90">
                  {!oauthConfig.gmail && !oauthConfig.outlook 
                    ? 'Email & Calendar OAuth integration is currently in development. The demo uses mock data for now.' 
                    : 'Connect at least one account to access real-time email rendering, calendar sync, and AI features.'}
                </p>
              </div>
            </div>
          </div>
          
          {oauthConfig.gmail && (
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5" />
                <div className="text-xs text-blue-500">
                  <p className="font-medium mb-1">Gmail OAuth Setup Required</p>
                  <p className="opacity-90 mb-2">
                    Before connecting Gmail, add your email as a test user in Google Cloud Console:
                  </p>
                  <ol className="opacity-90 space-y-1 ml-4 list-decimal">
                    <li>Visit: <span className="font-mono">console.cloud.google.com/apis/credentials/consent</span></li>
                    <li>Scroll to "Test users" → Click "ADD USERS"</li>
                    <li>Enter your Gmail address → Click "SAVE"</li>
                    <li>Try connecting Gmail again</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default EmailConnectionCard;
